import React, { useCallback, useEffect, useState } from 'react';
import { Card, CardContent, CircularProgress, Grid, Typography } from '@material-ui/core';
import { Button, Section, SmSpace } from 'm2m.components';
import moment from 'moment';
import { AxiosError } from 'axios';
import { useDispatch, useSelector } from 'react-redux';
import Chip from 'components/chip';
import { ErroApiMotorRiscos, EtapaFalhaKri, GrupoAcesso, RegraKri, ResultadoKri, ScoreKri } from 'common/genericTypes';
import { RootState } from 'store';
import { addAlert } from 'store/modules/snackBar';
import { formatNumber } from 'utils/number';

// Aba compartilhada entre os detalhes de todos os instrumentos financeiros
// que suportam o Motor de Riscos (Nota Comercial, Debênture, CRI, CRA, CDCA).

interface Props {
  instrumentoFinanceiroGuid: string;
}

type EstadoConsulta =
  | 'carregando'
  | 'ok'
  | 'nao-avaliado' // 204: instrumento existe mas nunca foi avaliado
  | 'nao-elegivel' // 404: instrumento inexistente/sem emissor/indexador não suportado
  | 'motor-indisponivel' // 502
  | 'erro-generico';

// Paleta do score baseada no theme do projeto (src/pages/app.tsx). Não temos "laranja"
// no theme; usei um tom coerente com a escala verde → amarelo → laranja → vermelho.
const CORES_SCORE: Record<ScoreKri, { background: string; foreground: string }> = {
  BAIXO: { background: '#00E08E', foreground: '#000' },
  MEDIO: { background: '#FFCF40', foreground: '#000' },
  ALTO: { background: '#FF9F43', foreground: '#000' },
  CRITICO: { background: '#FF5A5F', foreground: '#fff' },
};

// Ordem de severidade para exibição: do mais crítico ao mais baixo.
// Regras com status ERRO (sem score) vão para o final.
const ORDEM_SEVERIDADE: Record<ScoreKri, number> = {
  CRITICO: 0,
  ALTO: 1,
  MEDIO: 2,
  BAIXO: 3,
};
const ORDEM_SEM_SCORE = 4;

const CORES_NAO_CALCULADO = { background: '#BDBDBD', foreground: '#000' };

const ordenarRegrasPorSeveridade = (regras: RegraKri[]): RegraKri[] =>
  [...regras].sort((a, b) => {
    const ordemA = a.score ? ORDEM_SEVERIDADE[a.score] : ORDEM_SEM_SCORE;
    const ordemB = b.score ? ORDEM_SEVERIDADE[b.score] : ORDEM_SEM_SCORE;
    return ordemA - ordemB;
  });

const formatarPontuacao = (valor: number): string =>
  formatNumber(valor, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

// Mensagens para o 400 de insumo faltando no retry, por etapa.
const MENSAGEM_POR_ETAPA_FALHA: Record<EtapaFalhaKri, string> = {
  Entrada:
    'Cadastro do instrumento incompleto para avaliação de KRI. Verifique emissor, indexador e cronograma de amortização.',
  ValorFuturo: 'Não foi possível obter o valor futuro no Motor de Cálculo. Tente novamente em alguns minutos.',
  DurationPrazoMedio:
    'Não foi possível obter o prazo médio (duration) no Motor de Cálculo. Tente novamente em alguns minutos.',
  Curvas: 'Curva DI ou calendário de feriados indisponível no momento. Tente novamente em alguns minutos.',
  MotorRiscos: 'O Motor de Riscos não conseguiu processar a avaliação.',
};

const GRUPOS_COM_ESCRITA: GrupoAcesso[] = [
  GrupoAcesso.ControlesInternos,
  GrupoAcesso.Operacoes,
  GrupoAcesso.CadastroOperacoes,
];

const KRIsOperacao: React.FC<Props> = ({ instrumentoFinanceiroGuid }) => {
  const dispatch = useDispatch();

  const getKriApi = useSelector((state: RootState) => state.motorRiscosApi.getKriMotorRiscos);
  const postRetryApi = useSelector((state: RootState) => state.motorRiscosApi.postRetryKriMotorRiscos);
  const grupoAcesso = useSelector((state: RootState) => state.auth.usuario.grupoAcesso);

  const podeReavaliar = GRUPOS_COM_ESCRITA.includes(grupoAcesso);

  const [resultado, setResultado] = useState<ResultadoKri | null>(null);
  const [estado, setEstado] = useState<EstadoConsulta>('carregando');
  const [reavaliando, setReavaliando] = useState<boolean>(false);

  const carregarKri = useCallback(async () => {
    if (!instrumentoFinanceiroGuid) {
      // Sem GUID não há o que consultar. Fica em estado erro para não confundir com "nunca avaliado".
      setEstado('erro-generico');
      return;
    }

    setEstado('carregando');
    try {
      const result = await getKriApi(dispatch, instrumentoFinanceiroGuid, true);
      if (result) {
        setResultado(result);
        setEstado('ok');
      } else {
        setResultado(null);
        setEstado('nao-avaliado');
      }
    } catch (error) {
      const status = (error as AxiosError)?.response?.status;
      if (status === 404) {
        setEstado('nao-elegivel');
      } else if (status === 502) {
        setEstado('motor-indisponivel');
      } else {
        setEstado('erro-generico');
      }
    }
  }, [dispatch, getKriApi, instrumentoFinanceiroGuid]);

  useEffect(() => {
    carregarKri();
  }, [carregarKri]);

  const handleReavaliar = useCallback(async () => {
    if (!instrumentoFinanceiroGuid || reavaliando) return;

    setReavaliando(true);
    try {
      await postRetryApi(dispatch, instrumentoFinanceiroGuid, false);
      dispatch(addAlert({ severity: 'success', message: 'Reavaliação de KRI concluída.' }));
      // Recarrega a consulta — o número de execução vai ter incrementado.
      await carregarKri();
    } catch (error) {
      const axiosError = error as AxiosError<ErroApiMotorRiscos>;
      const status = axiosError.response?.status;
      const payload = axiosError.response?.data;
      const correlacao = payload?.correlationErrorId ? ` (ref.: ${payload.correlationErrorId})` : '';

      // Timeout do axios (aborted). O guia manda 90s, mas se estourar avisamos.
      if (axiosError.code === 'ECONNABORTED') {
        dispatch(
          addAlert({
            severity: 'error',
            message: 'A reavaliação demorou mais que o esperado e foi interrompida. Tente novamente em alguns minutos.',
          })
        );
      } else if (status === 409) {
        dispatch(
          addAlert({
            severity: 'info',
            message: 'Já existe uma avaliação de KRI em andamento para este instrumento.',
          })
        );
      } else if (status === 400 && payload?.etapaFalha) {
        dispatch(
          addAlert({
            severity: 'error',
            message: `${MENSAGEM_POR_ETAPA_FALHA[payload.etapaFalha]}${correlacao}`,
          })
        );
      } else if (status === 400) {
        dispatch(
          addAlert({
            severity: 'error',
            message: `${payload?.message ?? 'Não foi possível reavaliar o KRI.'}${correlacao}`,
          })
        );
      } else if (status === 502) {
        dispatch(
          addAlert({
            severity: 'error',
            message: `Motor de Riscos indisponível. Tente novamente em alguns minutos.${correlacao}`,
          })
        );
      } else {
        dispatch(
          addAlert({
            severity: 'error',
            message: `${payload?.message ?? 'Erro ao reavaliar o KRI.'}${correlacao}`,
          })
        );
      }
    } finally {
      setReavaliando(false);
    }
  }, [dispatch, postRetryApi, instrumentoFinanceiroGuid, reavaliando, carregarKri]);

  if (estado === 'carregando') {
    // O loader global (stackRequest) já cobre esse estado. Retornamos vazio para evitar flicker.
    return null;
  }

  if (estado === 'nao-avaliado') {
    return (
      <Section title={'KRIs da Operação'}>
        <Typography variant="body1">Este instrumento ainda não foi avaliado pelo Motor de Riscos.</Typography>
        <SmSpace />
        {podeReavaliar && (
          <Button
            variant="contained"
            color="primary"
            onClick={handleReavaliar}
            disabled={reavaliando}
            startIcon={reavaliando ? <CircularProgress size={16} color="inherit" /> : undefined}
          >
            {reavaliando ? 'Avaliando... pode levar até 90 segundos' : 'Avaliar KRI'}
          </Button>
        )}
      </Section>
    );
  }

  if (estado === 'nao-elegivel') {
    return (
      <Section title={'KRIs da Operação'}>
        <Typography variant="body1">
          Este instrumento não é elegível para avaliação de KRI. Isso pode ocorrer quando o cadastro está incompleto
          (sem emissor, com indexador não suportado ou sem cronograma de amortização).
        </Typography>
      </Section>
    );
  }

  if (estado === 'motor-indisponivel') {
    return (
      <Section title={'KRIs da Operação'}>
        <Typography variant="body1">
          O Motor de Riscos está indisponível no momento. Tente novamente em alguns minutos.
        </Typography>
        <SmSpace />
        {podeReavaliar && (
          <Button
            variant="contained"
            color="primary"
            onClick={handleReavaliar}
            disabled={reavaliando}
            startIcon={reavaliando ? <CircularProgress size={16} color="inherit" /> : undefined}
          >
            {reavaliando ? 'Reavaliando... pode levar até 90 segundos' : 'Tentar novamente'}
          </Button>
        )}
      </Section>
    );
  }

  if (estado === 'erro-generico' || !resultado) {
    return (
      <Section title={'KRIs da Operação'}>
        <Typography variant="body1">Não foi possível carregar o KRI deste instrumento.</Typography>
        <SmSpace />
        {podeReavaliar && (
          <Button
            variant="contained"
            color="primary"
            onClick={handleReavaliar}
            disabled={reavaliando}
            startIcon={reavaliando ? <CircularProgress size={16} color="inherit" /> : undefined}
          >
            {reavaliando ? 'Reavaliando... pode levar até 90 segundos' : 'Tentar novamente'}
          </Button>
        )}
      </Section>
    );
  }

  // estado === 'ok'
  const scoreGeral = resultado.resultado?.score;
  const valorGeral = resultado.resultado?.valor;
  const geralCalculado = Boolean(scoreGeral);
  const cores = scoreGeral ? CORES_SCORE[scoreGeral] : CORES_NAO_CALCULADO;
  const temErrosParciais = resultado.status === 'PROCESSADO_COM_ERROS';
  const qtdRegrasCalculadas = resultado.regras.filter((regra) => regra.status === 'OK').length;

  return (
    <>
      <Section title={'Resultado Consolidado'}>
        <Grid container spacing={4} alignItems="center">
          <Grid item>
            <Chip
              label={scoreGeral ?? 'Não calculado'}
              style={{
                backgroundColor: cores.background,
                color: cores.foreground,
                fontWeight: 'bold',
                minWidth: 96,
              }}
            />
          </Grid>
          <Grid item>
            <Typography variant="body2">{'Pontuação geral:'}</Typography>
            <Typography variant="body1">
              {geralCalculado && typeof valorGeral === 'number' ? formatarPontuacao(valorGeral) : '—'}
            </Typography>
            <Typography variant="caption" color="textSecondary">
              {geralCalculado
                ? `Consolidação das pontuações de ${qtdRegrasCalculadas} de ${resultado.regras.length} regras calculadas`
                : 'Nenhuma regra pôde ser calculada nesta execução'}
            </Typography>
          </Grid>
          <Grid item>
            <Typography variant="body2">{'Avaliado em:'}</Typography>
            <Typography variant="body1">{moment(resultado.processadoEm).format('DD/MM/YYYY HH:mm')}</Typography>
          </Grid>
          <Grid item>
            <Typography variant="body2">{'Execução:'}</Typography>
            <Typography variant="body1">{`#${resultado.execucao}`}</Typography>
          </Grid>
        </Grid>
        {temErrosParciais && (
          <>
            <SmSpace />
            <Typography variant="body2" style={{ color: '#FFCF40' }}>
              {geralCalculado
                ? 'Algumas regras não puderam ser calculadas por falta de insumo e ficaram fora da pontuação geral. Elas estão destacadas abaixo; corrija o cadastro e reavalie para incluí-las.'
                : 'Nenhuma regra pôde ser calculada por falta de insumo, por isso não há pontuação geral. Corrija o cadastro e reavalie o KRI.'}
            </Typography>
          </>
        )}
      </Section>

      <Section title={'Regras Avaliadas'}>
        <Grid container spacing={2} direction="column">
          {ordenarRegrasPorSeveridade(resultado.regras).map((regra, index) => (
            <Grid item xs={12} key={regra.regraVersaoId ?? `${regra.codigo}-${index}`}>
              <CardRegra regra={regra} />
            </Grid>
          ))}
        </Grid>
      </Section>
    </>
  );
};

interface CardRegraProps {
  regra: RegraKri;
}

const CardRegra: React.FC<CardRegraProps> = ({ regra }) => {
  const isErro = regra.status === 'ERRO';
  const coresScore = regra.score ? CORES_SCORE[regra.score] : undefined;

  return (
    <Card variant="outlined" style={{ borderColor: isErro ? '#FF5A5F' : undefined, height: '100%' }}>
      <CardContent>
        <Grid container spacing={1} alignItems="center" justifyContent="space-between">
          <Grid item xs>
            <Typography variant="body1">{regra.codigo}</Typography>
          </Grid>
          {regra.eliminatoria && (
            <Grid item>
              <Chip size="small" label="Eliminatória" color="default" />
            </Grid>
          )}
        </Grid>
        <SmSpace />
        {!isErro && regra.score && coresScore && (
          <Grid container spacing={2} alignItems="center">
            <Grid item>
              <Chip
                size="small"
                label={regra.score}
                style={{
                  backgroundColor: coresScore.background,
                  color: coresScore.foreground,
                  fontWeight: 'bold',
                  minWidth: 80,
                }}
              />
            </Grid>
            <Grid item>
              <Typography variant="body2">{'Pontuação:'}</Typography>
              <Typography variant="body1">
                {typeof regra.pontuacao === 'number' ? `${formatarPontuacao(regra.pontuacao)} pts` : '—'}
              </Typography>
            </Grid>
          </Grid>
        )}
        {isErro && (
          <>
            <Grid container spacing={2} alignItems="center">
              <Grid item>
                <Chip
                  size="small"
                  label="Não calculado"
                  style={{
                    backgroundColor: CORES_NAO_CALCULADO.background,
                    color: CORES_NAO_CALCULADO.foreground,
                    fontWeight: 'bold',
                    minWidth: 80,
                  }}
                />
              </Grid>
              <Grid item>
                <Typography variant="body2">{'Pontuação:'}</Typography>
                <Typography variant="body1">{'—'}</Typography>
              </Grid>
            </Grid>
            <SmSpace />
            <Typography variant="body2" style={{ color: '#FF5A5F', fontWeight: 'bold' }}>
              Esta regra não entrou na pontuação geral
            </Typography>
            {regra.mensagemErro && (
              <>
                <SmSpace />
                <Typography variant="body2">{regra.mensagemErro}</Typography>
              </>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default KRIsOperacao;
